import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import { Link } from "@/lib/router-compat";
import {
  RelatedPages,
  allEducationLinks,
  allTreatmentsLinks,
  allServiceLinks,
  allDecisionLinks,
} from "@/components/RelatedPages";
import {
  HelpCircle,
  CheckCircle2,
  AlertTriangle,
  ClipboardList,
  ShieldCheck,
  Brain,
  ArrowRight,
  Activity,
  BarChart3,
} from "lucide-react";
const IsTmsRightForMe = () => {
  const assessmentQuestions = [
    "Are you taking more than one antidepressant medication?",
    "Is your current medication failing to provide adequate benefit for your depression?",
    "Do the side effects from your medication outweigh the benefit?",
    "Has depression increased your doctor visits for other medical or healthcare conditions?",
    "Is depression affecting your ability to make a living or maintain employment?",
    "Does depression still have too much control over your daily life?",
  ];
  const intakeSteps = [
    {
      step: "01",
      title: "Complimentary Consultation",
      description:
        "Fill out our consultation form or obtain a physician referral. Meet with a Patient Consultant to review medical history and insurance coverage.",
    },
    {
      step: "02",
      title: "Psychiatric Pre-Assessment",
      description:
        "Meet with a Neuro Wellness Psychiatrist for a clinical evaluation to verify eligibility and confirm that TMS is appropriate for your diagnosis.",
    },
    {
      step: "03",
      title: "Personalized TMS Mapping",
      description:
        "Begin your customized treatment plan with motor threshold calibration and targeted neural coil placement.",
    },
  ];
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={
          "Is TMS Right For Me? | Candidate Assessment | Neuro Wellness TMS Centers of America"
        }
        description={
          "Find out if Transcranial Magnetic Stimulation (TMS) is right for your depression. Review candidacy criteria, antidepressant failure requirements, and insurance guidelines in Coral Springs, FL."
        }
        canonical={"/contents/tms/is-tms-right-for-me"}
        keywords={
          "Is TMS right for me, TMS candidate assessment, treatment resistant depression candidate, TMS contraindications, antidepressant failure requirement, TMS insurance qualification Coral Springs"
        }
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            name: "Is TMS Right For Me?",
            description:
              "Clinical candidacy assessment criteria for Transcranial Magnetic Stimulation (TMS) therapy in major depressive disorder.",
            url: "/contents/tms/is-tms-right-for-me/",
          },
        ]}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* Hero Banner */}
        <section className="relative overflow-hidden bg-gradient-navy text-primary-foreground py-16 sm:py-24">
          <div className="pointer-events-none absolute -right-20 top-0 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
          <div className="container-px mx-auto max-w-7xl">
            <div className="max-w-3xl space-y-4 animate-fade-up">
              <span className="inline-block rounded-full bg-accent/20 px-4 py-1.5 text-xs sm:text-sm font-bold text-accent uppercase tracking-wider">
                TMS Education • Candidate Self-Assessment
              </span>
              <h1 className="font-headings text-3xl font-extrabold text-white sm:text-5xl md:text-6xl">
                Is TMS Right For Me?
              </h1>
              <p className="text-lg sm:text-2xl text-accent font-semibold">
                Criteria, Qualifications & Medical Assessment for TMS Therapy
              </p>
              <p className="text-base sm:text-lg text-primary-foreground/80 leading-relaxed max-w-2xl">
                If traditional antidepressant medications have failed to relieve
                your depression symptoms or caused unbearable side effects,
                non-invasive TMS Therapy may be the right solution for you.
              </p>
              <div className="pt-4 flex flex-wrap gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
                >
                  <a href="#consultation">Schedule Free Consultation</a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/50 text-white bg-transparent hover:bg-white/10"
                >
                  <a href="tel:9548279793">Call 954-827-9793</a>
                </Button>
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-none pointer-events-none">
            <svg
              className="relative block w-full h-8 sm:h-12 text-background"
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
              fill="currentColor"
            >
              <path d="M0,0 C150,90 350,-40 500,50 C650,140 900,10 1200,40 L1200,120 L0,120 Z"></path>
            </svg>
          </div>
        </section>

        {/* Assessment Section */}
        <section className="py-12 sm:py-20 bg-background">
          <div className="container-px mx-auto max-w-5xl space-y-12">
            {/* Overview & FDA Indication */}
            <div className="rounded-2xl bg-card border border-border p-6 sm:p-10 shadow-soft space-y-6">
              <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary">
                FDA Indications & Insurance Coverage Guidelines
              </h2>
              <p className="text-base sm:text-lg text-foreground/90 leading-relaxed font-normal">
                TMS therapy is a non-invasive, non-drug, FDA-cleared treatment
                for individuals diagnosed with Major Depressive Disorder (MDD)
                who have failed to receive satisfactory improvement from at
                least 1 prior antidepressant medication.
              </p>
              <p className="text-base sm:text-lg text-foreground/90 leading-relaxed font-normal">
                However, for full commercial insurance or Medicare coverage,
                most insurance carriers require that a patient has tried and
                failed to achieve satisfactory improvement from 2 to 4 prior
                antidepressant medications from different classes, or was forced
                to discontinue due to adverse side effects.
              </p>

              {/* Contraindications Warning Box */}
              <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-5 space-y-2">
                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 font-bold text-base sm:text-lg">
                  <AlertTriangle className="h-5 w-5 shrink-0" />
                  <span>Important Medical Contraindications</span>
                </div>
                <p className="text-sm text-foreground/85 leading-relaxed font-normal">
                  TMS Therapy is contraindicated in individuals with implanted
                  metallic devices or non-removable metallic objects in or
                  around the head (such as aneurysm clips, metallic stents, or
                  shrapnel). Dental fillings, caps, and braces do not interfere
                  with TMS.
                </p>
              </div>
            </div>

            {/* Candidate Self-Assessment Checklist */}
            <div className="rounded-2xl bg-light-blue/30 border border-light-blue p-6 sm:p-10 space-y-6">
              <div className="max-w-3xl space-y-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-accent">
                  <ClipboardList className="h-4 w-4" />
                  Self-Assessment Questions
                </span>
                <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary">
                  Do You Qualify for TMS Therapy?
                </h2>
                <p className="text-sm sm:text-base text-foreground/85">
                  To determine if TMS is right for you, answer the questions
                  below. If you answer{" "}
                  <strong className="text-primary font-bold">
                    “YES” to two or more
                  </strong>{" "}
                  questions, TMS therapy may significantly benefit your
                  recovery:
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                {assessmentQuestions.map((q, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 bg-card p-4 rounded-xl border border-border shadow-xs hover:border-accent/40 transition-colors"
                  >
                    <CheckCircle2 className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                    <p className="text-sm sm:text-base text-primary font-medium">
                      {q}
                    </p>
                  </div>
                ))}
              </div>

              <div className="p-4 rounded-xl bg-card border border-border text-center space-y-3">
                <p className="text-sm font-semibold text-primary">
                  Answered "Yes" to 2 or more questions? Our clinical intake
                  team can verify your insurance coverage at no charge.
                </p>
                <Button
                  asChild
                  className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
                >
                  <a href="#consultation">Request Candidate Verification</a>
                </Button>
              </div>
            </div>

            {/* 3-Step Care Process */}
            <div className="space-y-6">
              <div className="text-center max-w-2xl mx-auto space-y-2">
                <h2 className="font-headings text-2xl sm:text-4xl font-extrabold text-primary">
                  Our Patient Care Process
                </h2>
                <p className="text-muted-foreground text-sm sm:text-base">
                  How we ensure the highest standard of psychiatric evaluation
                  and personalized care.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {intakeSteps.map((s, idx) => (
                  <div
                    key={idx}
                    className="rounded-2xl border border-border bg-card p-6 shadow-soft space-y-4 relative overflow-hidden"
                  >
                    <span className="text-4xl font-extrabold font-headings text-accent/20 absolute top-4 right-4">
                      {s.step}
                    </span>
                    <h3 className="font-headings text-xl font-bold text-primary">
                      {s.title}
                    </h3>
                    <p className="text-sm text-foreground/80 leading-relaxed font-normal">
                      {s.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Link directory to other TMS pages */}
            <div className="rounded-2xl bg-gradient-navy text-primary-foreground p-6 sm:p-10 space-y-6">
              <h3 className="font-headings text-xl sm:text-2xl font-bold text-white text-center">
                Learn More About TMS
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Link
                  to="/contents/tms/what-is-tms-therapy"
                  className="bg-white/10 hover:bg-white/20 p-4 rounded-xl border border-white/20 transition-all text-center space-y-2 group"
                >
                  <Brain className="h-6 w-6 text-accent mx-auto" />
                  <p className="font-bold text-white text-sm group-hover:text-accent transition-colors">
                    What Is TMS Therapy?
                  </p>
                  <p className="text-xs text-primary-foreground/75">
                    Brain circuitry & neurotransmitters
                  </p>
                </Link>

                <Link
                  to="/contents/tms/typical-tms-treatment"
                  className="bg-white/10 hover:bg-white/20 p-4 rounded-xl border border-white/20 transition-all text-center space-y-2 group"
                >
                  <Activity className="h-6 w-6 text-accent mx-auto" />
                  <p className="font-bold text-white text-sm group-hover:text-accent transition-colors">
                    Typical TMS Treatment
                  </p>
                  <p className="text-xs text-primary-foreground/75">
                    Frequency, duration & motor threshold
                  </p>
                </Link>

                <Link
                  to="/contents/tms/tms-clinical-study-results"
                  className="bg-white/10 hover:bg-white/20 p-4 rounded-xl border border-white/20 transition-all text-center space-y-2 group"
                >
                  <BarChart3 className="h-6 w-6 text-accent mx-auto" />
                  <p className="font-bold text-white text-sm group-hover:text-accent transition-colors">
                    Clinical Study Results
                  </p>
                  <p className="text-xs text-primary-foreground/75">
                    67%+ response rate data & remission
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Consultation Component */}
        <RelatedPages
          heading="Explore Related TMS Resources"
          links={[
            ...allEducationLinks.filter(
              (l) => l.href !== "/contents/tms/is-tms-right-for-me/",
            ),
            ...allTreatmentsLinks,
            ...allServiceLinks,
            ...allDecisionLinks,
          ]}
        />
        <Consultation />
      </main>

      <Footer />
    </div>
  );
};
export default IsTmsRightForMe;
